import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Paperclip, X, FilmSlate, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_VIDEO_SECONDS = 5;
const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm";

/** Reads the true duration of a video file in the browser before uploading. */
const readDuration = (file) =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = () => {
            const d = video.duration;
            URL.revokeObjectURL(url);
            Number.isFinite(d) && d > 0 ? resolve(d) : reject(new Error("unreadable"));
        };
        video.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("unreadable"));
        };
        video.src = url;
    });

/** Optional image / ≤5s video attachment. Uploads straight to object storage. */
export const AnnouncementMediaPicker = ({ media, onChange }) => {
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const inputRef = useRef(null);

    const pick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const isVideo = file.type.startsWith("video/");
        let duration = null;
        if (isVideo) {
            try {
                duration = await readDuration(file);
            } catch {
                return toast.error("That video's length could not be read. Please try an MP4 file.");
            }
            if (duration > MAX_VIDEO_SECONDS + 0.4) {
                return toast.error(
                    `Videos must be 5 seconds or shorter — this one is ${duration.toFixed(1)} seconds.`,
                );
            }
        }
        const body = new FormData();
        body.append("file", file);
        if (duration !== null) body.append("duration_seconds", duration.toFixed(2));
        setBusy(true);
        try {
            const { data } = await api.post("/announcements/media", body);
            onChange(data);
            setPreview({ url: URL.createObjectURL(file), type: data.type });
            toast.success("Attachment ready.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not upload the attachment");
        } finally {
            setBusy(false);
        }
    };

    const clear = () => {
        if (preview?.url) URL.revokeObjectURL(preview.url);
        setPreview(null);
        onChange(null);
    };

    return (
        <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                Attachment (optional)
            </Label>
            <input ref={inputRef} type="file" accept={ACCEPT} onChange={pick} className="hidden"
                data-testid="announcement-media-input" />
            {media ? (
                <div className="flex items-start gap-3 p-3 rounded-md border border-[#E2E8F0] bg-slate-50"
                    data-testid="announcement-media-attached">
                    {preview?.url && (preview.type === "video" ? (
                        <video src={preview.url} className="h-16 w-24 rounded object-cover bg-black" muted />
                    ) : (
                        <img src={preview.url} alt="" className="h-16 w-24 rounded object-cover" />
                    ))}
                    <div className="flex-1 min-w-0 text-sm">
                        <div className="flex items-center gap-1.5 font-medium text-[#1F2A44] truncate">
                            {media.type === "video" ? <FilmSlate size={14} /> : <ImageIcon size={14} />}
                            {media.original_filename}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                            {(media.size / 1024).toFixed(0)} KB
                            {media.duration_seconds ? ` · ${media.duration_seconds.toFixed(1)}s` : ""}
                        </div>
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={clear} className="text-[#E6002C]"
                        data-testid="announcement-media-remove">
                        <X size={14} />
                    </Button>
                </div>
            ) : (
                <div>
                    <Button type="button" variant="outline" disabled={busy}
                        onClick={() => inputRef.current?.click()}
                        className="border-[#E2E8F0] text-slate-600 h-10"
                        data-testid="announcement-media-pick-button">
                        <Paperclip size={15} className="mr-1.5" />
                        {busy ? "Uploading…" : "Attach photo or video"}
                    </Button>
                    <p className="text-[11px] text-slate-400 mt-1.5">
                        JPG, PNG, WEBP or GIF up to 8 MB · MP4, MOV or WEBM up to 20 MB and
                        <strong> 5 seconds maximum</strong>. Longer videos are rejected.
                    </p>
                </div>
            )}
        </div>
    );
};
